import React, { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef } from 'react'
import { reducer, initialState } from './reducer'
import { useLocalization } from '@/hooks/localization'
import { VariableSizeGrid } from 'react-window'
import {
  getCoordinatesFromDomElement,
  getRowSize,
  getSelectionProps, getTableHeight,
  leftOrRight,
  parseClipboardData,
  tableContentWidthCorrection
} from './utils'
import debounce from 'lodash/debounce'
import { TableRow } from './row'
import { Selection } from './selection'
import { TableTopHeader } from './topHeader'
import { ResizeHandle } from './resizeHandle'
import { useTableContext } from '@/components/report-tables/editable/editableTableContext'

const EditorContext = React.createContext({
  onChange: () => {},
  onSelectionChange: () => {},
  editorValue: '',
  selectionStart: 0,
  selectionEnd: 0,
  reverseTransform: () => {},
  reverseTransformed: false
})

const ReportTable = React.memo(function ReportTable (props) {
  const {
    sourceHeaders, sourceRowHeaders, sourceData, defaultMetadata, coordSpecificMetadata, cellMetadata, scrollToRow,
    onCellsChange, onSelectionChange, onEditorActive,
    renderer, headerRenderer, editor, readOnly,
    fixedHeader = true, blockUserInteraction = false, commentMode = 'readonly',
    persistenceId
  } = props

  const list = useRef(null)
  const { rtl, localizeValue } = useLocalization()
  const [state, dispatch] = useReducer(reducer, initialState)
  const {
    headerOffset,
    tableWidth,
    tableHeight,
    mouseDown,
    editorActive,
    editorMode,
    topHeaderRows,
    topHeaderRowCount,
    metadata,
    dataItems,
    colIds,
    rowIds,
    columnSizes,
    rowSizes,
    currentCol,
    currentColTreeCode,
    currentRow,
    currentRowTreeCode,
    selectionCol,
    selectionRow,
    collapsedRows,
    hiddenRows,
    changedCells,
    scrollToRowIndex,
    currentChangedData,
    inputSelectionStart,
    inputSelectionEnd,
    reverseTransformed
  } = state

  const { saveDataCells, changeEditorMode } = useTableContext()

  const cellsChangeEventDefined = useMemo(() => typeof saveDataCells === 'function' ||
    typeof onCellsChange === 'function', [onCellsChange, saveDataCells])

  const onCellsChangeInternal = useCallback(changes => {
    if (typeof saveDataCells === 'function') {
      saveDataCells(changes)
    }

    if (typeof onCellsChange === 'function') {
      onCellsChange(changes)
    }
  }, [onCellsChange, saveDataCells])

  const localStorageKey = persistenceId ? 'report-table-' + persistenceId : null
  const metadataLength = metadata ? metadata.length : 0
  const ref = useRef(null)

  // init table metadata
  useEffect(() => {
    // load persisted data
    let persistedData = {}
    if (localStorageKey && window.localStorage.getItem(localStorageKey)) {
      const data = JSON.parse(window.localStorage.getItem(localStorageKey))
      if (data) {
        persistedData = data
      }
    }
    const tableHeight = getTableHeight(ref)
    dispatch({ type: 'init', persistenceId, sourceHeaders, sourceRowHeaders, cellMetadata, defaultMetadata, fixedHeader, readOnly, localizeValue, tableHeight, ...persistedData })
  }, [cellMetadata, defaultMetadata, fixedHeader, readOnly, sourceHeaders, sourceRowHeaders, localizeValue, persistenceId, localStorageKey])

  // persist data on change
  useEffect(() => {
    if (localStorageKey) {
      setTimeout(() => {
        window.localStorage.setItem(localStorageKey, JSON.stringify({ columnSizes, tableHeight }))
      }, 0)
    }
  }, [localStorageKey, columnSizes, tableHeight])

  // set table data
  useEffect(() => {
    dispatch({ type: 'setData', sourceData })
  }, [sourceData])

  // onEditorActive event
  useEffect(() => {
    if (blockUserInteraction) {
      dispatch({ type: 'closeEditor' })
    }
    if (changeEditorMode !== null && typeof changeEditorMode === 'function') {
      setTimeout(() => changeEditorMode(editorMode), 0)
    }
    if (typeof onEditorActive === 'function') {
      setTimeout(() => {
        onEditorActive({
          col: currentCol,
          row: currentRow,
          colTreeCode: currentColTreeCode,
          rowTreeCode: currentRowTreeCode,
          editorMode,
          close: () => dispatch({ type: 'closeEditor' })
        })
      }, 0)
    }
  }, [blockUserInteraction, changeEditorMode, currentCol, currentColTreeCode, currentRow, currentRowTreeCode, editorMode, onEditorActive])

  // onCellsChange event
  useEffect(() => {
    if (changedCells && changedCells.length) {
      dispatch({ type: 'clearChanges' })
      onCellsChangeInternal(changedCells)
    }

    // Fixes issue of changedCells appearing again on rerender sometimes
    return () => dispatch({ type: 'clearChanges' })
    // Changed cells only grows or is set to empty so comparing only based on length
    // eslint-disable-next-line
  }, [cellsChangeEventDefined, changedCells.length, onCellsChangeInternal])

  // scroll to item
  useEffect(() => {
    dispatch({ type: 'scrollToRow', scrollToRow })
  }, [scrollToRow])
  useEffect(() => {
    if (list.current && typeof scrollToRowIndex === 'number' && scrollToRowIndex >= 0) {
      list.current.scrollToItem({ rowIndex: scrollToRowIndex })
    }
  }, [scrollToRowIndex])

  // scroll to current row when current row changes
  useEffect(() => {
    if (list.current) {
      list.current.scrollToItem({ rowIndex: currentRow || 0 })
    }
  }, [currentRow])

  // onSelectionChange event
  useEffect(() => {
    if (typeof onSelectionChange === 'function') {
      setTimeout(() => {
        onSelectionChange({
          colTreeCode: currentColTreeCode,
          rowTreeCode: currentRowTreeCode
        })
      }, 0)
    }
  }, [currentColTreeCode, currentRowTreeCode, onSelectionChange])

  const getRowHeight = useCallback((index) => {
    return getRowSize(rowSizes, hiddenRows, index, fixedHeader ? topHeaderRowCount : 0)
  }, [fixedHeader, hiddenRows, rowSizes, topHeaderRowCount])

  const getColumnWidth = useCallback(() => {
    return tableWidth + tableContentWidthCorrection
  }, [tableWidth])

  const onMouseDown = useCallback(e => {
    if (!blockUserInteraction) {
      const coords = getCoordinatesFromDomElement(e.target)
      if (coords) {
        dispatch({ type: 'mouseDown', ...coords })
      }
    }
  }, [blockUserInteraction])
  const onMouseMove = useCallback(e => {
    // Solves selecting although the left mouse button is not pressed
    if (e.buttons % 2 !== 1 && mouseDown) {
      dispatch({ type: 'mouseUp' })
      return
    }
    if (!blockUserInteraction && mouseDown) {
      const coords = getCoordinatesFromDomElement(e.target)
      if (coords) {
        dispatch({ type: 'select', ...coords })
      }
    }
  }, [blockUserInteraction, mouseDown])

  // Called from 2 different places due to more stable behaviour
  const onMouseUp = useCallback(_ => {
    dispatch({ type: 'mouseUp' })
  }, [])

  // keyboard operation handling
  useEffect(() => {
    const debounceKeyDown = debounce((e) => {
      const { key, shiftKey, altKey, ctrlKey, metaKey } = e
      switch (key) {
        case 'Delete':
        case 'Backspace':
        case 'ArrowUp':
        case 'ArrowDown':
        case 'ArrowLeft':
        case 'ArrowRight':
        case 'Enter':
        case 'Tab':
        case 'Escape':
          dispatch({ type: 'keydown', key, rtl, shiftKey, altKey, ctrlKey, metaKey })
          break
        default: {
          break
        }
      }
    }, 10, {
      'leading': true,
      'trailing': false
    })
    const onKeyDown = function (e) {
      const { key } = e
      switch (key) {
        case 'ArrowUp':
        case 'ArrowDown':
        case 'Enter':
        case 'Tab':
          e.preventDefault()
          break
      }
      debounceKeyDown(e)
    }

    const onKeyPress = function (e) {
      const { keyCode, shiftKey, altKey, ctrlKey, metaKey } = e
      const char = String.fromCharCode(keyCode)

      if (char) {
        dispatch({ type: 'keyPressed', value: char, shiftKey, altKey, ctrlKey, metaKey })
      }
    }

    const onCopy = function (e) {
      if (editorMode === 'none' && document.getSelection().type !== 'Range') {
        dispatch({ type: 'copy', clipboardData: e.clipboardData })
        e.preventDefault()
      }
    }

    const onPaste = function (e) {
      if (editorMode === 'none' && document.getSelection().type !== 'Range') {
        e.preventDefault()
        dispatch({ type: 'paste', data: parseClipboardData(e.clipboardData.getData('text/plain')) })
      }
    }

    if (!blockUserInteraction) {
      window.document.addEventListener('keydown', onKeyDown)
      window.document.addEventListener('keypress', onKeyPress)
      window.document.addEventListener('copy', onCopy)
      window.document.addEventListener('paste', onPaste)
      window.document.addEventListener('mouseup', onMouseUp)
    }
    // clean up
    return () => {
      if (!blockUserInteraction) {
        debounceKeyDown.cancel()
        window.document.removeEventListener('keydown', onKeyDown)
        window.document.removeEventListener('keypress', onKeyPress)
        window.document.removeEventListener('copy', onCopy)
        window.document.removeEventListener('paste', onPaste)
        window.document.removeEventListener('mouseup', onMouseUp)
      }
    }
  }, [blockUserInteraction, editorMode, onMouseUp, rtl])

  const onResize = useCallback(e => {
    dispatch({ type: 'resize', ...e })
  }, [])

  // row collapse support
  const onCollapseToggled = useCallback(({ row }) => {
    dispatch({ type: 'toggleCollapse', row })
  }, [])

  // force re-render react-window component after hiddenRows changes (it changes based on collapsed rows)
  // or topHeaderRowCount changes
  useLayoutEffect(() => {
    list.current.resetAfterRowIndex(0)
  }, [topHeaderRowCount, hiddenRows, metadataLength])

  const selectionProps = useMemo(() =>
    getSelectionProps(metadata, currentCol, currentRow, selectionCol, selectionRow, columnSizes, rowSizes, hiddenRows),
  [columnSizes, currentCol, currentRow, hiddenRows, metadata, rowSizes, selectionCol, selectionRow])

  const onScroll = useCallback(({ scrollLeft, scrollTop }) => {
    dispatch({ type: 'scroll', scrollLeft, scrollTop })
  }, [])

  const onColumnSizeChanged = useCallback(e => {
    dispatch({ ...e, type: 'columnSizeChanged' })
  }, [])

  const onChange = useCallback((change) => {
    if (cellsChangeEventDefined) {
      const col = colIds[currentColTreeCode]
      const row = rowIds[currentRowTreeCode]

      if (col && row) {
        if (editorActive) {
          dispatch({ type: 'updateCurrentChange', data: change })
        }
      }
    }
  }, [cellsChangeEventDefined, colIds, currentColTreeCode, currentRowTreeCode, editorActive, rowIds])

  const itemData = useMemo(() => {
    return {
      columnSizes,
      metadata,
      coordSpecificMetadata,
      readOnly,
      commentMode,
      dataItems,
      renderer,
      headerRenderer,
      editor,
      currentCol,
      currentRow,
      editorActive,
      editorMode,
      selectionCol,
      selectionRow,
      collapsedRows,
      hiddenRows,
      onCollapseToggled
    }
  }, [columnSizes, metadata, coordSpecificMetadata, readOnly, commentMode, dataItems, renderer, headerRenderer, editor, currentCol, currentRow, editorActive, editorMode, selectionCol, selectionRow, collapsedRows, hiddenRows, onCollapseToggled])

  const tableTopOffset = fixedHeader ? topHeaderRowCount * 25 : 0
  const TableBody = useMemo(() => ({ children, ...props }) => {
    return (
      <div {...props}>
        {selectionProps && <Selection {...selectionProps} topOffset={tableTopOffset} />}
        <div className='table-body'>{children}</div>
      </div>
    )
  }, [selectionProps, tableTopOffset])

  const containerStyle = useMemo(() => ({
    width: '100%',
    height: tableHeight,
    top: tableTopOffset,
    overflow: 'hidden',
    position: 'relative'
  }), [tableHeight, tableTopOffset])

  const fixedHeaderWrapperStyle = useMemo(() => ({ position: 'absolute', ...leftOrRight(rtl, -headerOffset) }), [headerOffset, rtl])
  return (
    <EditorContext.Provider value={{
      onChange: onChange,
      onSelectionChange: (start, end) => dispatch({ type: 'updateSelection', data: { start, end } }),
      editorValue: currentChangedData,
      selectionStart: inputSelectionStart,
      selectionEnd: inputSelectionEnd,
      reverseTransform: (f) => dispatch({ type: 'reverseTransform', data: f }),
      reverseTransformed: reverseTransformed }}>
      <div className='handsontable-wrap'>
        <div className='handsontable-wrap__inner'>
          <div
            ref={ref}
            className='linksoft-table'
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
          >
            {fixedHeader && <div style={fixedHeaderWrapperStyle}>
              {selectionProps && currentRow < topHeaderRowCount && <Selection {...selectionProps} />}
              <TableTopHeader metadata={topHeaderRows} globalMetadata={defaultMetadata} headerRenderer={headerRenderer} tableHeight={tableHeight} tableWidth={tableWidth} columnSizes={columnSizes} currentCol={currentCol} onColumnSizeChanged={onColumnSizeChanged} />
            </div>}
            <div
              style={containerStyle}
            >
              <VariableSizeGrid
                className='table-body-container'
                ref={list}
                direction={rtl ? 'rtl' : 'ltr'}
                width={tableWidth}
                height={tableHeight - tableTopOffset}
                innerElementType={TableBody}
                columnCount={1}
                rowCount={metadata ? metadata.length : 0}
                estimatedColumnWidth={tableWidth}
                estimatedRowHeight={25}
                rowHeight={getRowHeight}
                columnWidth={getColumnWidth}
                itemData={itemData}
                onScroll={onScroll}
                overscanRowCount={10}
              >{TableRow}</VariableSizeGrid>
            </div>
            <ResizeHandle tableHeight={tableHeight} onResize={onResize} />
          </div>
        </div>
      </div>
    </EditorContext.Provider>
  )
})

export {
  ReportTable, EditorContext
}



// WEBPACK FOOTER //
// ./src/components/report-tables/core/table.js